/**
 * Settings → Products and usage (prototype settingsProducts, editProduct, addClientProduct; L2062-2113, L3347-3357).
 *
 *  - "How usage is tracked" note.
 *  - 1. Products we sell: GET /products (name, kind, what we count, per, source chip, low/falling lines) and the
 *    number of clients buying each (GET /clients/{id}/products). "+ Add a product" / "Edit" open the product pop-up
 *    → POST /products · PATCH /products/{key}; a raised watch line is explained from `adjustment.reason`.
 *    The pop-up also edits "Report headers we accept" per number (GET/POST/DELETE /products/{key}/aliases), so a
 *    new spelling of a report column is accepted without a deploy.
 *  - 2. What each client bought: clients × products; a plan is typed in place (PATCH /clients/{id}/products/{cp})
 *    and "+ Add" opens the add pop-up (POST /clients/{id}/products).
 *  - 3. Where each client’s numbers come from: the per-client source (PATCH … source) and the feed state chip
 *    from GET /usage/overview.
 */
import { useMemo, useState } from 'react';
import {
  USAGE_SOURCE_LABEL,
  normaliseHeader,
  type ClientProduct,
  type ClientRow,
  type FeedState,
  type Product,
  type UsageSource,
} from '@amplispot/contracts';
import { useClients } from '../../lib/data';
import { useSession } from '../../lib/session';
import {
  useAliases,
  useCreateAlias,
  useCreateClientProduct,
  useCreateProduct,
  useDeleteAlias,
  useManyClientProducts,
  useProducts,
  useUpdateClientProduct,
  useUpdateProduct,
  useUsageOverview,
} from '../../lib/usage';
import { Empty, ErrorState, Field, Loading, Modal, errorText, useFail, useToast } from '../../ui/kit';

/** Prototype KINDS. `kind` is free text in the contract; an unknown one is shown as written. */
const KINDS: Record<string, { n: string; eg: string }> = {
  volume: { n: 'Volume', eg: 'Messages, calls or responses used out of what the plan includes' },
  active: { n: 'Active people', eg: 'Members, outlets or seats in use out of what is licensed' },
  running: { n: 'Things running', eg: 'Campaigns or journeys live out of what the plan allows' },
};
const kindName = (k: string) => KINDS[k]?.n ?? k;
const SOURCES = Object.keys(USAGE_SOURCE_LABEL) as UsageSource[];
/** Prototype SOURCES chip tones. */
const SOURCE_CHIP: Record<UsageSource, string> = { api: 'hot', csv: 'info', manual: '' };

export function ProductsUsage() {
  const products = useProducts();
  const clients = useClients();
  // GET /clients lists active clients by default.
  const active = useMemo(() => [...(clients.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [clients.data]);
  // One GET /client-products for every active client (t-c1371dd8), not one request per client.
  const cps = useManyClientProducts('active');
  const [editing, setEditing] = useState<Product | 'new' | null>(null);
  const [adding, setAdding] = useState<{ client: ClientRow; product: Product } | null>(null);

  const list = products.data ?? [];
  const buyers = (key: string) =>
    active.filter((c) => (cps.byClient.get(c.id) ?? []).some((p) => p.product_key === key && p.status !== 'ended')).length;

  return (
    <>
      <div className="note" style={{ marginBottom: 16 }}>
        <b>How usage is tracked.</b> Every product, whatever it counts, is reduced to three numbers for each client: what they{' '}
        <b>bought</b> each period, what they have <b>used</b> so far, and their own <b>usual level</b>. Health, the low-use and
        falling-use alerts, and the revenue-against-use chart all work from those three. Setting up a product takes three steps:
        define it, say what each client bought, and say where the numbers come from.
      </div>

      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="hd">
          <h3>1. Products we sell</h3>
          <div className="r">
            <button className="btn sm pri" onClick={() => setEditing('new')}>
              + Add a product
            </button>
          </div>
        </div>
        {products.isLoading ? (
          <Loading />
        ) : products.error ? (
          <div className="bd">
            <ErrorState error={products.error} onRetry={() => void products.refetch()} />
          </div>
        ) : !list.length ? (
          <div className="bd">
            <Empty title="No products yet">Add the first product you sell, then say which clients bought it.</Empty>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ minWidth: 760 }}>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>What we count</th>
                  <th>Per</th>
                  <th>Numbers come from</th>
                  <th>Low use below</th>
                  <th>Falling by</th>
                  <th>Clients</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((p) => (
                  <tr key={p.key}>
                    <td>
                      <b>{p.name}</b>
                      <div className="sub">{kindName(p.kind)}</div>
                    </td>
                    <td>
                      {p.main_metric} <span className="sub">({p.unit})</span>
                    </td>
                    <td>{p.period}</td>
                    <td>
                      <span className={`chip ${SOURCE_CHIP[p.source]}`}>{USAGE_SOURCE_LABEL[p.source].label}</span>
                    </td>
                    <td className="num">{p.low_line}%</td>
                    <td className="num">{p.fall_pct}%</td>
                    <td className="num">{cps.loading ? '…' : buyers(p.key)}</td>
                    <td className="tr">
                      <button className="btn sm" onClick={() => setEditing(p)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ClientPlans
        products={list}
        clients={active}
        cps={cps}
        loading={clients.isLoading || products.isLoading}
        error={clients.error ?? products.error}
        retry={() => void clients.refetch()}
        onAdd={(client, product) => setAdding({ client, product })}
      />
      <ClientSources products={list} clients={active} cps={cps} loading={clients.isLoading || products.isLoading} />

      {editing ? <ProductModal product={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
      {adding ? <AddClientProductModal {...adding} onClose={() => setAdding(null)} /> : null}
    </>
  );
}

type Cps = ReturnType<typeof useManyClientProducts>;

// ---------------------------------------------------------------- 2. What each client bought

function ClientPlans(props: {
  products: Product[];
  clients: ClientRow[];
  cps: Cps;
  loading: boolean;
  error: unknown;
  retry: () => void;
  onAdd: (c: ClientRow, p: Product) => void;
}) {
  const { products, clients, cps } = props;
  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="hd">
        <h3>2. What each client bought</h3>
        <div className="r">The amount included each period. Type to change it.</div>
      </div>
      {props.loading || (cps.loading && !cps.byClient.size) ? (
        <Loading />
      ) : props.error || cps.error ? (
        <div className="bd">
          <ErrorState error={props.error ?? cps.error} onRetry={props.retry} />
        </div>
      ) : !clients.length || !products.length ? (
        <div className="bd">
          <Empty title={!products.length ? 'No products yet' : 'No active clients'}>
            {!products.length ? 'Add a product above first.' : 'Clients appear here once they are added.'}
          </Empty>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ minWidth: 180 + products.length * 140 }}>
            <thead>
              <tr>
                <th>Client</th>
                {products.map((p) => (
                  <th key={p.key}>
                    {p.name}
                    <div className="sub" style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                      {p.unit} per {p.period}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id}>
                  <td>
                    <b>{c.name}</b>
                  </td>
                  {products.map((p) => {
                    const mine = cps.productsOf(c.id);
                    const cp = mine?.find((x) => x.product_key === p.key);
                    return (
                      <td key={p.key}>
                        {cp ? (
                          <PlanInput client={c} cp={cp} />
                        ) : mine ? (
                          <button className="btn sm ghost" onClick={() => props.onAdd(c, p)}>
                            + Add
                          </button>
                        ) : (
                          <span className="sub">…</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PlanInput({ client, cp }: { client: ClientRow; cp: ClientProduct }) {
  const update = useUpdateClientProduct();
  const toast = useToast();
  const [v, setV] = useState(cp.plan == null ? '' : String(cp.plan));
  const save = () => {
    const n = parseFloat(v);
    // Same rule as the Add pop-up: a plan must include more than 0 (a 0 plan has no utilisation).
    if (v.trim() === '' || isNaN(n) || n <= 0) {
      setV(cp.plan == null ? '' : String(cp.plan));
      if (v.trim() !== '' || cp.plan != null) toast(`Enter what the plan includes, more than 0. ${cp.product_name} for ${client.name} was not changed.`);
      return;
    }
    if (n === cp.plan) return;
    update.mutate(
      { clientId: client.id, cpId: cp.id, body: { plan: n } },
      {
        onSuccess: () => toast('Plan saved'),
        onError: (e) => {
          setV(cp.plan == null ? '' : String(cp.plan));
          toast(errorText(e));
        },
      },
    );
  };
  return (
    <div>
      <input
        className="in"
        type="number"
        min={1}
        style={{ width: 110 }}
        value={v}
        aria-label={`${cp.product_name} plan for ${client.name}`}
        disabled={update.isPending}
        onChange={(e) => setV(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
      />
      {cp.status !== 'active' ? <div className="sub">{cp.status === 'paused' ? 'Paused' : 'Ended'}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------- 3. Where each client’s numbers come from

function ClientSources(props: { products: Product[]; clients: ClientRow[]; cps: Cps; loading: boolean }) {
  const { products, clients, cps } = props;
  const overview = useUsageOverview();
  const feed = useMemo(() => {
    const m = new Map<string, FeedState>();
    for (const r of overview.data?.clients ?? []) for (const c of r.cells) m.set(`${r.client.id}:${c.product_key}`, c.feed_state);
    return m;
  }, [overview.data]);
  // Stale data from an earlier load still counts; only a failure with nothing to show is 'error'.
  const stateStatus: FeedStatus = overview.data ? 'ready' : overview.error ? 'error' : 'loading';
  const byKey = new Map(products.map((p) => [p.key, p]));
  const rows = clients.flatMap((c) =>
    (cps.byClient.get(c.id) ?? []).filter((cp) => cp.status !== 'ended' && byKey.has(cp.product_key)).map((cp) => ({ c, cp })),
  );

  return (
    <div className="panel">
      <div className="hd">
        <h3>3. Where each client’s numbers come from</h3>
        <div className="r">Automatic feeds need no work. The others create a weekly task for the SPOC.</div>
      </div>
      {props.loading || (cps.loading && !rows.length) ? (
        <Loading />
      ) : cps.error ? (
        <div className="bd">
          <ErrorState error={cps.error} />
        </div>
      ) : !rows.length ? (
        <div className="bd">
          <Empty title="No client buys a product yet">Add what each client bought above, then choose where its numbers come from.</Empty>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          {stateStatus === 'error' ? (
            <div className="note warn split" style={{ margin: '0 16px 12px', alignItems: 'center' }} role="alert">
              <span>
                <b>The state of each feed did not load.</b> {errorText(overview.error)} You can still change where the numbers come from.
              </span>
              <div className="grow" />
              <button className="btn sm" onClick={() => void overview.refetch()} disabled={overview.isFetching}>
                {overview.isFetching ? 'Trying…' : 'Try again'}
              </button>
            </div>
          ) : null}
          <table style={{ minWidth: 560 }}>
            <thead>
              <tr>
                <th>Client</th>
                <th>Product</th>
                <th>Numbers come from</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ c, cp }) => (
                <SourceRow
                  key={cp.id}
                  client={c}
                  cp={cp}
                  product={byKey.get(cp.product_key)!}
                  state={feed.get(`${c.id}:${cp.product_key}`)}
                  stateStatus={stateStatus}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

type FeedStatus = 'loading' | 'error' | 'ready';

function SourceRow(props: { client: ClientRow; cp: ClientProduct; product: Product; state: FeedState | undefined; stateStatus: FeedStatus }) {
  const { client, cp, product, state } = props;
  const update = useUpdateClientProduct();
  const toast = useToast();
  const current = cp.source ?? product.source;
  return (
    <tr>
      <td>{client.name}</td>
      <td>{product.name}</td>
      <td>
        <select
          className="in"
          style={{ width: 'auto', padding: '4px 8px' }}
          value={current}
          disabled={update.isPending}
          aria-label={`Where ${client.name}’s ${product.name} numbers come from`}
          onChange={(e) => {
            const s = e.target.value as UsageSource;
            update.mutate(
              { clientId: client.id, cpId: cp.id, body: { source: s } },
              {
                onSuccess: () => toast(`Source changed to ${USAGE_SOURCE_LABEL[s].label.toLowerCase()}`),
                onError: (err) => toast(errorText(err)),
              },
            );
          }}
        >
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {USAGE_SOURCE_LABEL[s].label}
            </option>
          ))}
        </select>
      </td>
      <td>
        {state ? (
          <span className={`chip ${state.tone}`}>{state.label}</span>
        ) : props.stateStatus === 'loading' ? (
          <span className="sub">…</span>
        ) : props.stateStatus === 'error' ? (
          <span className="chip">State not available</span>
        ) : (
          // The overview loaded and has no cell for this client-product: nothing has arrived yet.
          <span className="chip info">Waiting for first data</span>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------- add a product to a client

function AddClientProductModal({ client, product, onClose }: { client: ClientRow; product: Product; onClose: () => void }) {
  const { can } = useSession();
  const revenue = can('revenue.read');
  const create = useCreateClientProduct();
  const { errs, setErrs, fail, toast } = useFail(['plan', 'source', 'mrr_lakh']);
  const [plan, setPlan] = useState('');
  const [source, setSource] = useState<UsageSource>(product.source);
  const [mrr, setMrr] = useState('0');

  const save = () => {
    const n = parseFloat(plan);
    if (isNaN(n) || n <= 0) {
      setErrs({ plan: 'Enter what the plan includes' });
      return;
    }
    const m = parseFloat(mrr);
    create.mutate(
      {
        clientId: client.id,
        body: { product_key: product.key, plan: n, source, ...(revenue && !isNaN(m) && m > 0 ? { mrr_lakh: m } : {}) },
      },
      {
        onSuccess: () => {
          toast('Added. Waiting for the first figure.');
          onClose();
        },
        onError: fail,
      },
    );
  };

  return (
    <Modal
      title={`Add ${product.name} for ${client.name}`}
      sub="What did they buy, and how do the numbers reach us?"
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={save} disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add'}
          </button>
        </>
      }
    >
      <Field label={`Included each ${product.period} (${product.unit})`} error={errs.plan}>
        <input className="in" type="number" min={0} value={plan} onChange={(e) => setPlan(e.target.value)} autoFocus />
      </Field>
      <Field label="Numbers come from" error={errs.source}>
        <select className="in" value={source} onChange={(e) => setSource(e.target.value as UsageSource)}>
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {USAGE_SOURCE_LABEL[s].label}: {USAGE_SOURCE_LABEL[s].help}
            </option>
          ))}
        </select>
      </Field>
      {revenue ? (
        <Field label="Monthly price (₹ lakh)" hint="Only you and the Ops associate director can see this" error={errs.mrr_lakh}>
          <input className="in" type="number" min={0} step={0.1} value={mrr} onChange={(e) => setMrr(e.target.value)} />
        </Field>
      ) : null}
    </Modal>
  );
}

// ---------------------------------------------------------------- add or edit a product

/** A product key from its name (prototype saveProduct): lower-case letters and digits, starting with a letter. */
function keyFrom(name: string, taken: (k: string) => boolean): string {
  let k = name.toLowerCase().replace(/[^a-z0-9]+/g, '').replace(/^[^a-z]+/, '').slice(0, 12);
  if (k.length < 2) k = `p${Date.now().toString(36)}`;
  let out = k;
  for (let i = 2; taken(out); i++) out = `${k}${i}`;
  return out;
}
/** A field key from its label: lower-case words joined by _ (the contract's ^[a-z][a-z0-9_]{1,30}$). */
function fieldKeyFrom(label: string): string {
  let k = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^[^a-z]+|_+$/g, '').slice(0, 31);
  if (k.length < 2) k = `f_${k || 'x'}`;
  return k === 'main' ? 'main_2' : k;
}

const PRODUCT_FIELDS = ['name', 'kind', 'main_metric', 'unit', 'period', 'source', 'low_line', 'fall_pct', 'fields'] as const;

function ProductModal({ product, onClose }: { product: Product | null; onClose: () => void }) {
  const all = useProducts().data ?? [];
  const create = useCreateProduct();
  const update = useUpdateProduct();
  const { errs, setErrs, fail, toast } = useFail(PRODUCT_FIELDS);
  const [name, setName] = useState(product?.name ?? '');
  const [kind, setKind] = useState(product?.kind ?? 'volume');
  const [metric, setMetric] = useState(product?.main_metric ?? '');
  const [unit, setUnit] = useState(product?.unit ?? '');
  const [period, setPeriod] = useState<'month' | 'week'>(product?.period ?? 'month');
  const [source, setSource] = useState<UsageSource>(product?.source ?? 'api');
  const [low, setLow] = useState(String(product?.low_line ?? 40));
  const [fall, setFall] = useState(String(product?.fall_pct ?? 15));
  const extrasOf = (p: Product | null) => (p?.fields ?? []).filter((f) => !f.is_main);
  const [extras, setExtras] = useState(extrasOf(product).map((f) => f.label).join(', '));
  const pending = create.isPending || update.isPending;

  const buildFields = () => {
    const old = extrasOf(product);
    const seen = new Set<string>();
    return extras
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length >= 2)
      .map((label) => {
        const o = old.find((f) => f.label.toLowerCase() === label.toLowerCase());
        let k = o ? o.field_key : fieldKeyFrom(label);
        for (let i = 2; seen.has(k); i++) k = `${fieldKeyFrom(label).slice(0, 28)}_${i}`;
        seen.add(k);
        return { field_key: k, label, ...(o?.unit ? { unit: o.unit } : {}), ...(o ? { is_confirmed: o.is_confirmed } : {}) };
      });
  };

  const save = () => {
    const n = name.trim(),
      m = metric.trim(),
      u = unit.trim();
    if (!n || !m || !u) {
      setErrs({
        ...(n ? {} : { name: 'Add a name' }),
        ...(m ? {} : { main_metric: 'Say what we count' }),
        ...(u ? {} : { unit: 'Add the unit' }),
      });
      return;
    }
    const lowN = parseFloat(low),
      fallN = parseFloat(fall);
    const done = (p: Product, msg: string) => {
      toast(p.adjustment ? `${msg} ${p.adjustment.reason}` : msg);
      onClose();
    };
    const fields = buildFields();
    if (product) {
      update.mutate(
        {
          key: product.key,
          body: {
            name: n,
            main_metric: m,
            unit: u,
            source,
            ...(isNaN(lowN) ? {} : { low_line: lowN }),
            ...(isNaN(fallN) ? {} : { fall_pct: fallN }),
            fields,
          },
        },
        { onSuccess: (p) => done(p, 'Product saved.'), onError: fail },
      );
    } else {
      const key = keyFrom(n, (k) => all.some((p) => p.key === k));
      create.mutate(
        {
          key,
          name: n,
          kind,
          main_metric: m,
          unit: u,
          period,
          source,
          low_line: isNaN(lowN) ? 40 : lowN,
          fall_pct: isNaN(fallN) ? 15 : fallN,
          ...(fields.length ? { fields } : {}),
        },
        { onSuccess: (p) => done(p, `${n} added. Now say which clients bought it.`), onError: fail },
      );
    }
  };

  return (
    <Modal
      wide
      title={product ? `Edit ${product.name}` : 'Add a product'}
      sub="Tell the system what this product counts and where the numbers come from. That is all it needs."
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={save} disabled={pending}>
            {pending ? 'Saving…' : product ? 'Save' : 'Add product'}
          </button>
        </>
      }
    >
      <Field label="Product name" error={errs.name}>
        <input className="in" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Training Reels" />
      </Field>
      <div className="lbl" style={{ marginBottom: 6 }}>
        What kind of usage is it?
      </div>
      <div className="popts" role="radiogroup" aria-label="What kind of usage is it?">
        {Object.entries(KINDS).map(([k, v]) => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={kind === k}
            className={`popt${kind === k ? ' on' : ''}`}
            disabled={!!product && kind !== k}
            onClick={() => setKind(k)}
          >
            <b style={{ fontSize: 13 }}>{v.n}</b>
            <small>{v.eg}</small>
          </button>
        ))}
      </div>
      {product ? <small className="fhint">The kind and the period are set when a product is added.</small> : null}
      {errs.kind ? <small className="ferr">{errs.kind}</small> : null}
      <div className="cols c2" style={{ marginTop: 12 }}>
        <Field label="What do we count?" error={errs.main_metric}>
          <input className="in" value={metric} onChange={(e) => setMetric(e.target.value)} placeholder="e.g. Videos watched" />
        </Field>
        <Field label="Unit" error={errs.unit}>
          <input className="in" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="e.g. views" />
        </Field>
      </div>
      <Field label="Plan is measured per" error={errs.period}>
        <select className="in" value={period} disabled={!!product} onChange={(e) => setPeriod(e.target.value as 'month' | 'week')}>
          <option value="month">Month</option>
          <option value="week">Week</option>
        </select>
      </Field>
      <div className="lbl" style={{ marginBottom: 6 }}>
        Where do the numbers come from?
      </div>
      <div className="popts" role="radiogroup" aria-label="Where do the numbers come from?">
        {SOURCES.map((s) => (
          <button
            key={s}
            type="button"
            role="radio"
            aria-checked={source === s}
            className={`popt${source === s ? ' on' : ''}`}
            onClick={() => setSource(s)}
          >
            <b style={{ fontSize: 13 }}>{USAGE_SOURCE_LABEL[s].label}</b>
            <small>{USAGE_SOURCE_LABEL[s].help}</small>
          </button>
        ))}
      </div>
      <div className="cols c2" style={{ marginTop: 12 }}>
        <Field label="Low use below (% of plan)" error={errs.low_line}>
          <input className="in" type="number" min={0} max={90} value={low} onChange={(e) => setLow(e.target.value)} />
        </Field>
        <Field label="Falling by (% vs own usual)" error={errs.fall_pct}>
          <input className="in" type="number" min={0} max={100} value={fall} onChange={(e) => setFall(e.target.value)} />
        </Field>
      </div>
      <Field label="Other numbers to record (separate with commas, e.g. Unique visitors, Leads captured)" error={errs.fields}>
        <input
          className="in"
          value={extras}
          onChange={(e) => setExtras(e.target.value)}
          placeholder="e.g. Unique visitors, Leads captured, Shares"
        />
      </Field>
      {product ? (
        <HeaderAliases product={product} />
      ) : (
        <div className="note" style={{ marginTop: 12, marginBottom: 0 }}>
          Once the product is added, open <b>Edit</b> to list the report column headers we accept for each number.
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------- report headers we accept (aliases)

function HeaderAliases({ product }: { product: Product }) {
  const aliases = useAliases(product.key);
  const create = useCreateAlias();
  const del = useDeleteAlias();
  const { errs, setErrs, fail, toast } = useFail(['alias', 'product_field_id']);
  const fields = product.fields;
  const [fieldId, setFieldId] = useState(fields[0]?.id ?? '');
  const [alias, setAlias] = useState('');
  const norm = normaliseHeader(alias);
  const list = aliases.data ?? [];
  const taken = !!norm && list.some((a) => a.alias_normalised === norm);

  const add = () => {
    if (!norm) {
      setErrs({ alias: 'Type a header with letters or digits in it' });
      return;
    }
    if (taken) {
      setErrs({ alias: 'Already an accepted header for this product' });
      return;
    }
    create.mutate(
      { key: product.key, body: { product_field_id: fieldId, alias: alias.trim() } },
      {
        onSuccess: (a) => {
          toast(`Reports with a “${a.alias}” column now fill ${a.field_label}.`);
          setAlias('');
          setErrs({});
        },
        onError: fail,
      },
    );
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div className="lbl" style={{ marginBottom: 6 }}>
        Report headers we accept
      </div>
      <p className="sub" style={{ margin: '0 0 10px', fontSize: 12.5 }}>
        When a report’s column header matches one of these, its numbers go into that field. Case, spaces and punctuation are
        ignored. Changes apply to the next upload.
      </p>
      {aliases.isLoading ? (
        <Loading />
      ) : aliases.error ? (
        <ErrorState error={aliases.error} onRetry={() => void aliases.refetch()} />
      ) : (
        <div className="panel" style={{ marginBottom: 10 }}>
          <table>
            <thead>
              <tr>
                <th>Number</th>
                <th>Headers we accept</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((f) => {
                const mine = list.filter((a) => a.product_field_id === f.id);
                return (
                  <tr key={f.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <b>{f.label}</b>
                      {f.is_main ? <div className="sub">Main number</div> : null}
                    </td>
                    <td>
                      {mine.length ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {mine.map((a) => (
                            <span key={a.id} className="chip" title={`Matches “${a.alias_normalised}”`}>
                              {a.alias}
                              <button
                                type="button"
                                className="btn ghost"
                                style={{ padding: '0 2px', minHeight: 0, fontSize: 12, lineHeight: 1 }}
                                aria-label={`Stop accepting “${a.alias}”`}
                                disabled={del.isPending}
                                onClick={() =>
                                  del.mutate(
                                    { key: product.key, id: a.id },
                                    { onSuccess: () => toast(`“${a.alias}” removed.`), onError: (e) => toast(errorText(e)) },
                                  )
                                }
                              >
                                ✕
                              </button>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="sub">Only “{f.label}” itself</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="cols c2" style={{ alignItems: 'start' }}>
        <Field label="Number" error={errs.product_field_id}>
          <select className="in" value={fieldId} onChange={(e) => setFieldId(e.target.value)}>
            {fields.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Header in the report"
          error={errs.alias}
          hint={norm ? (taken ? 'Already accepted for this product' : `Will match “${norm}”`) : 'e.g. Page Views, Total msgs'}
        >
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="in"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  add();
                }
              }}
              placeholder="e.g. Page Views"
            />
            <button type="button" className="btn" style={{ whiteSpace: 'nowrap' }} onClick={add} disabled={create.isPending || !fieldId}>
              Add header
            </button>
          </div>
        </Field>
      </div>
    </div>
  );
}
